<template>
  <BaseFilters
    :bem="bemBlock"
    :filter-display-name="filterDisplayName"
    :is-select-multiple="false"
    :show="range && range.length == 2"
  >
    <template #content>
      <VueSlider
        v-model="value"
        :class="bemElement('control')"
        :drag-on-click="true"
        :disabled="loading"
        :contained="true"
        :dot-size="11"
        :enable-cross="false"
        :height="3"
        :min="rangeMin"
        :max="rangeMax"
        tooltip="none"
        @drag-end="emitNewFilteredRange"
      />
      <p :class="bemElement('info')">
        <span :class="bemElement('from')">{{ formattedValueFrom }}</span>
        <span class="visually-hidden">-</span>
        <span :class="bemElement('to')">{{ formattedValueTo }}</span>
      </p>
    </template>
  </BaseFilters>
</template>

<script>
import VueSlider from 'vue-slider-component';
import createPrice from '../../common/models/priceModel';
import bemClassesMixin from '../mixins/bemClassesMixin';
import BaseFilters from './BaseFilters.vue';
import { arrayEquals } from '../../common/utils/arrayUtils';
import getFrontConfig from '../../common/getFrontConfig';

const { defaultCurrency } = getFrontConfig();

export default {
  name: 'BaseFiltersPrice',

  components: {
    BaseFilters,
    VueSlider,
  },

  mixins: [bemClassesMixin],

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    refinement: {
      type: Object,
      default: () => ({}),
    },
    selected: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    currency: '',
    priceFormatOptions: { style: 'decimal' },
    value: null,
  }),

  computed: {
    filterDisplayName() {
      return `${this.$msg('global.price', 'locale')} (${this.currency})`;
    },
    /**
     * Formatted price value for minimum
     * @returns {string} Formatted price value for minimum
     */
    formattedValueFrom() {
      return createPrice(this.value?.[0], this.priceFormatOptions).formatted;
    },
    /**
     * Formatted price value for max
     * @returns {string} Formatted price value for max
     */
    formattedValueTo() {
      return createPrice(this.value?.[1], this.priceFormatOptions).formatted;
    },
    /**
     * Greatest range available in array. Equivalent to returning all products.
     * @returns {[number, number]} min and max pair
     */
    range() {
      return this.getRangeFromRefinementValue(this.valueForFullRange);
    },
    /**
     * Maximum limit available.
     * @returns {number} max
     */
    rangeMax() {
      return this.range?.[1] ?? 0;
    },
    /**
     * Minimum limit available.
     * @returns {number} min
     */
    rangeMin() {
      return this.range?.[0] ?? 0;
    },
    /**
     * Selected range in array format
     * @returns {[number, number]} min and max pair
     */
    selectedRange() {
      const selection = this.selected?.find(
        (filter) => 'valueFrom' in filter && 'valueTo' in filter
      );
      return this.getRangeFromRefinementValue(selection) ?? this.range;
    },
    /**
     * Refinement value for maximum range
     * @returns {ProductSearchRefinementValue} Refinement value for maximum range
     */
    valueForFullRange() {
      return this.refinement?.values?.[0];
    },
  },

  watch: {
    selectedRange() {
      this.value = this.selectedRange;
    },
  },

  mounted() {
    this.value = this.selectedRange;
    this.currency = defaultCurrency;
  },

  methods: {
    /**
     * Emit the deselection of any price refinement
     * @returns {undefined}
     */
    deselectFilter() {
      this.$emit('deselect');
    },
    /**
     * Get a simple range array from a ProductSearchRefinementValue
     * @param {ProductSearchRefinementValue} val - Refinement value for price
     * @returns {[number, number]} min and max pair
     */
    getRangeFromRefinementValue(val) {
      let min = val?.valueFrom ?? -1;
      let max = val?.valueTo ?? -1;
      min = parseInt(min, 10);
      max = parseInt(max, 10);
      return min >= 0 && min <= max ? [min, max] : null;
    },
    /**
     * Emit selected filter if value is different from full range
     * Otherwise, emit deselect filter
     */
    emitNewFilteredRange() {
      if (!this.value || this.isAllProductsRange(this.value)) {
        // Full range is equal to not filtering by price
        this.deselectFilter();
      } else {
        const selectedFilter = {
          ...this.valueForFullRange,
          selected: true,
          valueFrom: this.value[0],
          valueTo: this.value[1],
          displayValueFrom: this.value[0],
          displayValueTo: this.value[1],
        };
        this.selectFilter(selectedFilter);
      }
    },
    /**
     * Returns true if the given range is the greatest, so all the products should
     * be found.
     * @param {[number, number]} range min and max pair
     * @returns {boolean} true or false
     */
    isAllProductsRange(range) {
      return arrayEquals(this.range, range);
    },
    /**
     * Emit the selection of a selected ProductSearchRefinementValue
     * @param {ProductSearchRefinementValue} value - Refinement value for price
     * @returns {undefined}
     */
    selectFilter(value) {
      this.$emit('select', value);
    },
  },
};
</script>
